import {Box, Button, Card, CardContent, Container, Grid, Typography} from '@mui/material';
import {useAuth} from '@saltus/auth';
import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import MfaPanel from '../../components/Mfa/MfaPanel';
import MfaIcon from '../../theme/styled-components/MfaIcon';
import SmsIcon from '../../theme/styled-components/SmsIcon';
import {themeColors} from '../../theme/Theme';
import Loading from '../../components/Loading/Loading';
import {useMFAError} from '../../hooks/useMFAError';

const classes = {
  body: {
    color: themeColors.white,
    fontWeight: 300,
    marginBottom: '16px',
  },

  button: {
    backgroundColor: themeColors.coral,
    border: `2px solid ${themeColors.coral}`,
    color: themeColors.white,
    fontWeight: 400,
    padding: '5px 60px',
    textTransform: 'none',
    '&:disabled': {
      backgroundColor: themeColors.backgroundHighlight,
      border: `2px solid ${themeColors.backgroundHighlight}`,
      color: themeColors.white,
    },
    '&:hover': {
      backgroundColor: themeColors.backgroundHighlight,
      border: `2px solid ${themeColors.backgroundHighlight}`,
    },
  },

  heading: {
    fontSize: '30px',
    marginBottom: '32px',
  },

  icon: {
    margin: '0 auto 16px',
  },

  panel: {
    backgroundColor: themeColors.backgroundDark,
    boxShadow: 'none',
    height: '100%',
    padding: '16px 32px',
    textAlign: 'center',
  },

  panelContainer: {
    marginTop: '15px',
  },

  panelContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'space-between',
  },

  panelHeading: {
    color: themeColors.white,
    fontSize: '24px',
    marginBottom: '16px',
  },
};

function MfaEnrolmentPage() {
  const {getPreferredMFA, getUserDetails, user} = useAuth();
  const navigate = useNavigate();
  const [mfa, setMfa] = useState<string | null>(null);
  const [mobile, setMobile] = useState<string | null>(null);
  const methodIsSet = (method: string) => mfa === method;

  const {mfaError} = useMFAError();
  useEffect(() => {
    if (!mfa) {
      getPreferredMFA().then(setMfa).catch(mfaError);
    }
  }, [getPreferredMFA, mfa]);

  useEffect(() => {
    getUserDetails<{
      mobile: string;
    }>()
      .then((details) => {
        if (details) {
          setMobile(details.mobile);
        }
      })
      .catch(() => {});
  }, []);

  if (!user) {
    return <Loading />;
  }

  return (
    <Box>
      <Container maxWidth="lg">
        <MfaPanel title="Two-factor authentication">
          <Typography sx={classes.body} variant="body1">
            Two-factor authenticaction adds an extra layer of protection on login, which
            not only secures your account, but your clients too. Once enabled, each time
            you sign in you will be asked to enter a username, password and two
            factor-authetication code.
          </Typography>

          <Typography variant="body1">
            Please select a setup method (this can be changed later on):
          </Typography>

          <Grid
            container
            sx={classes.panelContainer}
            alignItems="stretch"
            spacing={3}
            justifyContent="center"
          >
            <Grid item md={6}>
              <Card sx={classes.panel}>
                <CardContent sx={classes.panelContent}>
                  <SmsIcon style={classes.icon} fill="#325A7D" />

                  <Typography sx={classes.panelHeading} component="h2" variant="h6">
                    Text message (SMS)
                  </Typography>

                  <Typography sx={classes.body} variant="body1">
                    Get a unique code via text message for each login. Easy to set up, but
                    relies on mobile network/wifi availability.
                  </Typography>

                  <div>
                    <Button
                      sx={classes.button}
                      type="button"
                      disabled={!mobile || methodIsSet('SMS_MFA')}
                      variant="outlined"
                      onClick={() => navigate('/2fa-enrolment/sms')}
                    >
                      {methodIsSet('SMS_MFA') ? 'Already active' : 'Use this method'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </Grid>

            <Grid item md={6}>
              <Card sx={classes.panel}>
                <CardContent sx={classes.panelContent}>
                  <MfaIcon style={classes.icon} fill="#325A7D" />

                  <Typography sx={classes.panelHeading} component="h2" variant="h6">
                    Authenticator app
                  </Typography>

                  <Typography sx={classes.body} variant="body1">
                    Use an app to generate a unique code for each login. Provides enhanced
                    security and works offline, but requires initial setup.
                  </Typography>

                  <div>
                    <Button
                      sx={classes.button}
                      type="button"
                      disabled={methodIsSet('SOFTWARE_TOKEN_MFA')}
                      variant="outlined"
                      onClick={() => navigate('/2fa-enrolment/app')}
                    >
                      {methodIsSet('SOFTWARE_TOKEN_MFA')
                        ? 'Already active'
                        : 'Use this method'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </MfaPanel>
      </Container>
    </Box>
  );
}

export default MfaEnrolmentPage;
